import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from "d3-force";
import type { MiroFishGraphEdge, MiroFishGraphNode } from "@/types/mirofish";

type SimNode = MiroFishGraphNode & { x: number; y: number; vx?: number; vy?: number };

type SimLink = {
  source: string;
  target: string;
  edge: MiroFishGraphEdge;
};

export const layoutMiroFishGraph = ({
  nodes,
  edges,
  width,
  height,
  spread = 1,
  jitter = 0,
}: {
  nodes: MiroFishGraphNode[];
  edges: MiroFishGraphEdge[];
  width: number;
  height: number;
  spread?: number;
  jitter?: number;
}): Map<string, { x: number; y: number }> => {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const simNodes: SimNode[] = nodes.map((node, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(nodes.length, 1) - Math.PI / 2;
    const radius = Math.min(width, height) * 0.22 * spread;
    return {
      ...node,
      x:
        width / 2 +
        Math.cos(angle) * radius +
        (Math.sin(index * 17.31) * jitter * width) / 2,
      y:
        height / 2 +
        Math.sin(angle) * radius +
        (Math.cos(index * 11.71) * jitter * height) / 2,
    };
  });

  const degree = new Map<string, number>();
  for (const node of simNodes) {
    degree.set(node.id, 0);
  }
  const simLinks: SimLink[] = [];
  for (const edge of edges) {
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) {
      continue;
    }
    simLinks.push({ source: edge.source, target: edge.target, edge });
    degree.set(edge.source, (degree.get(edge.source) || 0) + 1);
    degree.set(edge.target, (degree.get(edge.target) || 0) + 1);
  }

  const maxDegree = Math.max(1, ...Array.from(degree.values()));
  const simulation = forceSimulation<SimNode>(simNodes)
    .force(
      "link",
      forceLink<SimNode, SimLink>(simLinks)
        .id((d) => d.id)
        .distance(86 * spread)
        .strength(0.42)
    )
    .force("charge", forceManyBody<SimNode>().strength(-220 * spread))
    .force("center", forceCenter(width / 2, height / 2))
    .force(
      "collide",
      forceCollide<SimNode>()
        .radius((d) => 10 + (18 * (degree.get(d.id) || 0)) / maxDegree)
        .strength(0.9)
    )
    .alphaDecay(0.04)
    .velocityDecay(0.35);

  for (let i = 0; i < 320; i += 1) {
    simulation.tick();
    if (simulation.alpha() < 0.02) {
      break;
    }
  }
  simulation.stop();

  const positions = new Map<string, { x: number; y: number }>();
  for (const node of simNodes) {
    positions.set(node.id, { x: node.x, y: node.y });
  }

  return positions;
};
